import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PortalShell } from "@/components/layout/PortalShell";
import { AGENT_NAV } from "@/lib/agent-nav";
import { PortalPage } from "@/components/portal/PortalPage";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Seal } from "@/components/brand/Seal";

interface TrainingModule {
  id: string;
  title: string;
  description: string | null;
  duration_minutes: number | null;
  content_url: string | null;
  order_index: number;
}

interface TrainingProgress {
  module_id: string;
  completed: boolean;
  score: number | null;
  completed_at: string | null;
}

export const Route = createFileRoute("/agent/training")({
  head: () => ({ meta: [{ title: "Training — Strategist Portal" }, { name: "robots", content: "noindex" }] }),
  component: () => (
    <ProtectedRoute requiredRole="agent">
      <PortalShell title="Strategist Portal" nav={AGENT_NAV} tourKey="agent_tour">
        <AgentTraining />
      </PortalShell>
    </ProtectedRoute>
  ),
});

function AgentTraining() {
  const { user } = useAuth();
  const [modules, setModules] = useState<TrainingModule[]>([]);
  const [progress, setProgress] = useState<TrainingProgress[]>([]);

  useEffect(() => {
    if (!user) return;
    void loadTraining();
  }, [user?.id]);

  async function loadTraining() {
    if (!user) return;
    const [modulesRes, progressRes] = await Promise.all([
      supabase
        .from("training_modules")
        .select("id, title, description, duration_minutes, content_url, order_index")
        .eq("published", true)
        .in("audience", ["agent", "all"])
        .order("order_index", { ascending: true }),
      supabase
        .from("training_progress")
        .select("module_id, completed, score, completed_at")
        .eq("user_id", user.id),
    ]);
    setModules(modulesRes.data ?? []);
    setProgress(progressRes.data ?? []);
  }

  const progressByModule = useMemo(
    () => new Map(progress.map((row) => [row.module_id, row])),
    [progress],
  );
  const completedCount = progress.filter((row) => row.completed).length;
  const totalMinutes = modules.reduce((sum, module) => sum + (module.duration_minutes ?? 0), 0);

  return (
    <PortalPage eyebrow="Academy" title="Training" intro="Published strategist curriculum, progress, and completion status from the training table.">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Seal title={String(modules.length)} subtitle="Modules" size="sm" />
        <Seal title={String(completedCount)} subtitle="Completed" size="sm" />
        <Seal title={`${totalMinutes}`} subtitle="Training minutes" size="sm" />
      </div>

      <div className="bg-white border border-bronze-600/20 rounded-[4px] overflow-hidden shadow-soft">
        {modules.length === 0 ? (
          <div className="p-10 text-center">
            <h3 className="font-display text-navy-800 text-[22px]">No published training modules</h3>
            <p className="mt-2 font-sans text-[14px] text-charcoal-500">When admin publishes strategist training, it appears here automatically.</p>
          </div>
        ) : (
          <ul className="divide-y divide-bronze-600/15">
            {modules.map((module) => {
              const row = progressByModule.get(module.id);
              return (
                <li key={module.id} className="p-5 flex items-start justify-between gap-5">
                  <div>
                    <p className="font-sans text-[10px] uppercase tracking-[0.18em] text-bronze-600">Module {module.order_index}</p>
                    <h3 className="mt-1 font-display text-navy-800 text-[20px]">{module.title}</h3>
                    {module.description && <p className="mt-2 font-sans text-[13px] text-charcoal-500 leading-relaxed">{module.description}</p>}
                    <p className="mt-3 font-sans text-[12px] text-charcoal-500">
                      {module.duration_minutes ?? 0} min · {row?.completed ? `Completed${row.score !== null ? ` · Score ${row.score}` : ""}` : "Not completed"}
                    </p>
                  </div>
                  {module.content_url && (
                    <a href={module.content_url} target="_blank" rel="noreferrer" className="shrink-0 font-sans text-[11px] font-semibold uppercase tracking-[0.18em] text-bronze-600 hover:text-navy-800">
                      Open →
                    </a>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </PortalPage>
  );
}